/**
 * 路由目录 — path / 标题 / 侧栏投影 / RouteGuard access 的唯一数据源。
 *
 * Callers: navigation 投影、layout RouteGate、角色编辑树、登录落地
 *
 * Modules:
 * - workspaceRoutePolicies / adminRoutePolicies — 目录行
 * - getWorkspaceRoutePolicy / getAdminRoutePolicy — 按 path 查找
 * - resolveWorkspaceLandingPath — 无 overview 权限时的作业页落地
 */

import type { PlatformRole, WorkspacePermission } from "@metaadsmanager/tk-master-contracts";
import { Boxes3, Calculator, CreditCard, FileText, Gear, House, LayoutCells, PersonNutHex, PersonPlus, Persons, Picture, ShieldCheck, ShieldKeyhole, ShoppingBag, Target } from "@gravity-ui/icons";
import type { AppIcon } from "@/components/ui/app-icon";

import {
  resolveRouteAccess,
  type ProtectedRoute,
  type RouteMembership,
} from "@/lib/auth/route-access";
import { selectActiveMembership } from "@/lib/auth/workspace-actor";

import {
  normalizeApplicationPathname,
  toStaticExportHref,
} from "./normalize-application-pathname";

export type ApplicationMode = "workspace" | "admin";

export type ApplicationRoute = Readonly<{
  navigationHref?: string;
  pathname: string;
  titleKey: string;
}>;

/** Page access fields consumed by layout RouteGate / explicit RouteGuard. */
export type RoutePolicyAccess = Readonly<{
  requiredPermission?: WorkspacePermission;
  requiresWorkspaceOwner?: boolean;
  route: ProtectedRoute;
}>;

export type RouteNavGroup =
  | "primary"
  | "tools"
  | "team"
  | "secondary"
  | "admin";

export type RoleEditorKind = "alwaysOn" | "membership" | "assignable";

export type RouteNavSpec = Readonly<{
  group: RouteNavGroup;
  icon: AppIcon;
  labelKey: string;
  order: number;
}>;

export type RoutePolicy = Readonly<
  ApplicationRoute & {
    access: RoutePolicyAccess;
    nav?: RouteNavSpec;
    roleEditor?: RoleEditorKind;
    roleEditorBranch?: "nav:overview" | "nav:operations" | "nav:team";
    roleEditorLeafId?: string;
    /** 暂停时侧栏是否保留该叶子（与 pauseExempt/audit 对齐）。 */
    visibleWhenPaused?: boolean;
  }
>;

/**
 * Workspace path → title / sidebar highlight / RouteGuard access.
 * Visibility (sidebar) is projected in navigation.ts from `nav` + `visibleWhenPaused`.
 */
export const workspaceRoutePolicies: readonly RoutePolicy[] = [
  {
    pathname: "/",
    navigationHref: "/",
    titleKey: "dashboard.title",
    access: { route: "workspace", requiredPermission: "dashboard.view" },
    nav: {
      group: "primary",
      icon: House,
      labelKey: "shell.workspaceNavigation.overview",
      order: 10,
    },
    roleEditor: "alwaysOn",
    roleEditorBranch: "nav:overview",
    roleEditorLeafId: "leaf:dashboard",
  },
  {
    pathname: "/workspace/business-centers",
    navigationHref: "/workspace/business-centers",
    titleKey: "tiktokBusinessCenters.title",
    access: { route: "pauseExempt" },
    visibleWhenPaused: true,
  },
  {
    pathname: "/workspace/advertiser-accounts",
    navigationHref: "/workspace/advertiser-accounts",
    titleKey: "tiktokAdvertiserAccounts.title",
    access: { route: "pauseExempt" },
    nav: {
      group: "primary",
      icon: CreditCard,
      labelKey: "shell.workspaceNavigation.advertiserAccounts",
      order: 30,
    },
    roleEditor: "membership",
    roleEditorBranch: "nav:operations",
    roleEditorLeafId: "leaf:advertiserAccounts",
    visibleWhenPaused: true,
  },
  {
    pathname: "/workspace/stores",
    navigationHref: "/workspace/stores",
    titleKey: "tiktokStores.title",
    access: { route: "pauseExempt" },
    nav: {
      group: "primary",
      icon: ShoppingBag,
      labelKey: "shell.workspaceNavigation.stores",
      order: 40,
    },
    roleEditor: "membership",
    roleEditorBranch: "nav:operations",
    roleEditorLeafId: "leaf:stores",
    visibleWhenPaused: true,
  },
  {
    pathname: "/workspace/creative-assets",
    navigationHref: "/workspace/creative-assets",
    titleKey: "tiktokCreativeAssets.title",
    access: { route: "pauseExempt" },
    nav: {
      group: "primary",
      icon: Picture,
      labelKey: "shell.workspaceNavigation.creativeAssets",
      order: 50,
    },
    roleEditor: "membership",
    roleEditorBranch: "nav:operations",
    roleEditorLeafId: "leaf:creativeAssets",
    visibleWhenPaused: true,
  },
  {
    pathname: "/workspace/profit-calculator",
    navigationHref: "/workspace/profit-calculator",
    titleKey: "profitCalculator.title",
    access: { route: "workspace" },
    nav: {
      group: "tools",
      icon: Calculator,
      labelKey: "shell.workspaceNavigation.profitCalculator",
      order: 10,
    },
  },
  {
    pathname: "/workspace/roi-calculator",
    navigationHref: "/workspace/roi-calculator",
    titleKey: "roiCalculator.title",
    access: { route: "workspace" },
    nav: {
      group: "tools",
      icon: Target,
      labelKey: "shell.workspaceNavigation.roiCalculator",
      order: 20,
    },
  },
  {
    pathname: "/workspace/campaigns",
    navigationHref: "/workspace/campaigns",
    titleKey: "shell.workspaceNavigation.campaigns",
    access: { route: "pauseExempt" },
    nav: {
      group: "primary",
      icon: LayoutCells,
      labelKey: "shell.workspaceNavigation.campaigns",
      order: 20,
    },
    roleEditor: "membership",
    roleEditorBranch: "nav:operations",
    roleEditorLeafId: "leaf:campaigns",
    visibleWhenPaused: true,
  },
  {
    pathname: "/workspace/audiences",
    navigationHref: "/workspace/audiences",
    titleKey: "shell.workspaceNavigation.audiences",
    access: { route: "workspace" },
  },
  {
    pathname: "/workspace/members",
    navigationHref: "/workspace/members",
    titleKey: "governance.title",
    access: {
      route: "workspace",
      requiredPermission: "workspace.members.view",
    },
    nav: {
      group: "team",
      icon: PersonNutHex,
      labelKey: "shell.workspaceNavigation.members",
      order: 10,
    },
    roleEditor: "assignable",
    roleEditorBranch: "nav:team",
    roleEditorLeafId: "leaf:members",
  },
  {
    pathname: "/workspace/roles",
    navigationHref: "/workspace/roles",
    titleKey: "governance.roles.title",
    access: { route: "workspace", requiresWorkspaceOwner: true },
    nav: {
      group: "team",
      icon: ShieldKeyhole,
      labelKey: "shell.workspaceNavigation.roles",
      order: 15,
    },
  },
  {
    pathname: "/workspace/audit-events",
    navigationHref: "/workspace/audit-events",
    titleKey: "audit.title",
    access: {
      route: "audit",
      requiredPermission: "workspace.audit.view",
    },
    nav: {
      group: "team",
      icon: FileText,
      labelKey: "shell.workspaceNavigation.audit",
      order: 20,
    },
    roleEditor: "assignable",
    roleEditorBranch: "nav:team",
    roleEditorLeafId: "leaf:audit",
    visibleWhenPaused: true,
  },
  {
    pathname: "/workspace/integrations",
    navigationHref: "/workspace/integrations",
    titleKey: "tiktokIntegration.title",
    access: { route: "pauseExempt" },
    nav: {
      group: "secondary",
      icon: ShieldCheck,
      labelKey: "shell.workspaceNavigation.integrations",
      order: 20,
    },
    visibleWhenPaused: true,
  },
  {
    pathname: "/workspace/referrals",
    navigationHref: "/workspace/referrals",
    titleKey: "referrals.title",
    access: { route: "account" },
    nav: {
      group: "secondary",
      icon: PersonPlus,
      labelKey: "shell.workspaceNavigation.referrals",
      order: 25,
    },
    visibleWhenPaused: true,
  },
  {
    pathname: "/workspace/paused",
    titleKey: "paused.title",
    access: { route: "paused" },
  },
  {
    pathname: "/settings",
    navigationHref: "/settings",
    titleKey: "settings.title",
    access: { route: "account" },
    nav: {
      group: "secondary",
      icon: Gear,
      labelKey: "shell.workspaceNavigation.settings",
      order: 30,
    },
    visibleWhenPaused: true,
  },
  {
    pathname: "/onboarding",
    titleKey: "onboarding.title",
    access: { route: "onboarding" },
  },
  {
    pathname: "/auth/invitation",
    titleKey: "invitation.loading",
    access: { route: "invitation" },
  },
];

export const adminRoutePolicies: readonly RoutePolicy[] = [
  {
    pathname: "/admin/workspaces",
    navigationHref: "/admin/workspaces",
    titleKey: "admin.workspaces.title",
    access: { route: "admin" },
    nav: {
      group: "admin",
      icon: Boxes3,
      labelKey: "shell.adminNavigation.workspaces",
      order: 10,
    },
  },
  {
    pathname: "/admin/users",
    navigationHref: "/admin/users",
    titleKey: "admin.users.title",
    access: { route: "admin" },
    nav: {
      group: "admin",
      icon: Persons,
      labelKey: "shell.adminNavigation.users",
      order: 20,
    },
  },
  {
    pathname: "/admin/audit-events",
    navigationHref: "/admin/audit-events",
    titleKey: "admin.audit.title",
    access: { route: "admin" },
    nav: {
      group: "admin",
      icon: FileText,
      labelKey: "shell.adminNavigation.audit",
      order: 30,
    },
  },
  {
    pathname: "/settings",
    titleKey: "settings.title",
    access: { route: "account" },
  },
];

const GROUP_RANK: Record<RouteNavGroup, number> = {
  primary: 0,
  tools: 1,
  team: 2,
  secondary: 3,
  admin: 9,
};

function findPolicy(
  policies: readonly RoutePolicy[],
  pathname: string,
): RoutePolicy | undefined {
  const normalized = normalizeApplicationPathname(pathname);
  // 旧书签 /dashboard 与概览 `/` 共用同一条 policy（高亮 + Guard）。
  const key = normalized === "/dashboard" ? "/" : normalized;
  return policies.find((policy) => policy.pathname === key);
}

export function getWorkspaceRoutePolicy(
  pathname: string,
): RoutePolicy | undefined {
  return findPolicy(workspaceRoutePolicies, pathname);
}

export function getAdminRoutePolicy(pathname: string): RoutePolicy | undefined {
  return findPolicy(adminRoutePolicies, pathname);
}

/** 按 URL 选目录：/admin 用管理端，其余用工作区（含 /settings）。 */
export function getRoutePolicyForPathname(
  pathname: string,
): RoutePolicy | undefined {
  const normalized = normalizeApplicationPathname(pathname);
  if (normalized.startsWith("/admin")) return getAdminRoutePolicy(normalized);
  return getWorkspaceRoutePolicy(normalized);
}

/** Title / highlight projection for the application shell. */
export function resolveApplicationRouteFromPolicies(
  mode: ApplicationMode,
  pathname: string,
): ApplicationRoute | undefined {
  const policy =
    mode === "admin"
      ? getAdminRoutePolicy(pathname)
      : getWorkspaceRoutePolicy(pathname);
  if (!policy) return undefined;
  const {
    access: _access,
    nav: _nav,
    roleEditor: _roleEditor,
    roleEditorBranch: _roleEditorBranch,
    roleEditorLeafId: _roleEditorLeafId,
    visibleWhenPaused: _visibleWhenPaused,
    ...route
  } = policy;
  return route;
}

/** 超管在 /settings 仍用管理端壳；/admin 前缀始终管理端。 */
export function resolveApplicationShellMode(
  pathname: string,
  platformRole: PlatformRole | null | undefined,
): ApplicationMode {
  const normalized = normalizeApplicationPathname(pathname);
  if (normalized.startsWith("/admin")) return "admin";
  if (normalized === "/settings" && platformRole === "super_admin") {
    return "admin";
  }
  return "workspace";
}

/**
 * 登录 / 回调落地：暂停 → paused；有 overview 权限 → `/`；否则第一条允许的作业页。
 */
export function resolveWorkspaceLandingPath(
  membership: RouteMembership | null,
): string {
  if (!membership) return "/onboarding";
  if (membership.status === "paused") return "/workspace/paused";

  const candidates = workspaceRoutePolicies
    .filter(
      (policy) =>
        policy.nav &&
        policy.access.route !== "account" &&
        policy.nav.group !== "admin",
    )
    .slice()
    .sort((left, right) => {
      const groupDelta =
        GROUP_RANK[left.nav!.group] - GROUP_RANK[right.nav!.group];
      if (groupDelta !== 0) return groupDelta;
      return left.nav!.order - right.nav!.order;
    });

  for (const policy of candidates) {
    const decision = resolveRouteAccess({
      authenticated: true,
      membership,
      platformRole: null,
      requiredPermission: policy.access.requiredPermission,
      requiresWorkspaceOwner: policy.access.requiresWorkspaceOwner,
      route: policy.access.route,
    });
    if (decision.action === "allow") return policy.pathname;
  }

  return "/settings";
}

type PostAuthMe = {
  memberships: ReadonlyArray<{
    permissions: WorkspacePermission[];
    role: RouteMembership["role"];
    workspace: { id: string; status: RouteMembership["status"] };
  }>;
  platformRole: PlatformRole | null;
};

/** 登录与 OAuth 回调共用的身份落地。优先使用当前选中的工作空间。 */
export function resolvePostAuthPath(
  me: PostAuthMe,
  preferredWorkspaceId?: string | null,
): string {
  if (me.platformRole === "super_admin") return "/admin/workspaces";
  const membership = selectActiveMembership(
    me.memberships,
    preferredWorkspaceId,
  ).membership;
  if (!membership) return "/onboarding";
  return resolveWorkspaceLandingPath({
    permissions: membership.permissions,
    role: membership.role,
    status: membership.workspace.status,
  });
}

/** 身份落地 href（含静态导出尾斜杠），避免先落到 `/` 再由门闸二次跳转。 */
export function toPostAuthHref(
  me: PostAuthMe,
  preferredWorkspaceId?: string | null,
): string {
  return toStaticExportHref(resolvePostAuthPath(me, preferredWorkspaceId));
}
